/**
 * Chart payload embedded in assistant markdown as ```trodo-chart fenced JSON.
 *
 * @typedef {Object} TrodoChartSeriesItem
 * @property {string} name — Legend label.
 * @property {string} dataKey — Key on each `data` row for this series (numeric).
 * @property {string} [color] — Optional CSS color (e.g. hex).
 */

/**
 * @typedef {Object} TrodoChartSpec
 * @property {number} version — Must be `1`.
 * @property {'bar'|'line'|'area'} kind
 * @property {string} title
 * @property {string} xKey — Dimension column key (categories or time labels).
 * @property {TrodoChartSeriesItem[]} series — At least one series.
 * @property {Record<string, unknown>[]} data — At least one row; each row includes `xKey` and each series `dataKey`.
 */

/**
 * Chart kinds with the standard `{xKey, series, data}` shape (one numeric
 * dataKey per series, one row per category/time bucket).
 *
 * - bar / line / area / pie / funnel — original kinds.
 * - donut — pie variant with hollow centre, same payload as `pie`.
 * - scatter — series[].dataKey is the y metric; xKey can be string or numeric.
 * - heatmap — single series; rows form a 2-D grid using xKey + series[0].dataKey.
 *   (Renderer treats the single dataKey column as cell intensity. Future row
 *   grouping can extend the shape.)
 *
 * Kinds that need full custom shapes (sankey, retention_matrix) are not yet
 * wired — declared here so the validator accepts them while we ship the
 * renderer.
 *
 * @type {ReadonlyArray<'bar' | 'line' | 'area' | 'pie' | 'donut' | 'funnel' | 'scatter' | 'heatmap'>}
 */
export const SUPPORTED_KINDS = Object.freeze([
  "bar",
  "line",
  "area",
  "pie",
  "donut",
  "funnel",
  "scatter",
  "heatmap",
  "flow_paths",
  "retention_matrix",
]);

/** Kinds that allow extra non-numeric fields on each row (e.g. target, label). */
export const PERMISSIVE_ROW_KINDS = Object.freeze(["flow_paths", "retention_matrix"]);

/**
 * Normalize LLM key labels to stable camelCase for Recharts `dataKey` / row keys.
 * Handles "Step Label", "Conversion Rate", "conversion_rate", "Count", "stepLabel".
 * @param {string} label
 * @returns {string}
 */
export function normalizeLabelKey(label) {
  const s = String(label).trim();
  if (!s) return "";
  if (/\s/.test(s)) {
    const parts = s.split(/\s+/).filter(Boolean);
    return (
      parts[0].toLowerCase() +
      parts.slice(1).map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join("")
    );
  }
  if (s.includes("_")) {
    const parts = s.split("_").filter(Boolean);
    return (
      parts[0].toLowerCase() +
      parts.slice(1).map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join("")
    );
  }
  return s.charAt(0).toLowerCase() + s.slice(1);
}

/**
 * Strip fence noise before JSON.parse.
 * @param {string} raw
 * @returns {string}
 */
export function sanitizeTrodoChartJsonText(raw) {
  let s = String(raw).trim();
  s = s.replace(/^[`]+|[`]+$/g, "");
  s = s.replace(/\u200b/g, "").trim();
  return s;
}

/**
 * Coerce a series cell to a finite number for Recharts.
 * Strips `,` and `%`, uses parseFloat — keeps 41.4 from "41.4%" (does not divide by 100).
 * @param {unknown} v
 * @returns {number}
 */
export function coerceTrodoChartMetricValue(v) {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    let t = v.trim().replace(/,/g, "");
    if (t === "") return NaN;
    if (t.endsWith("%")) {
      t = t.slice(0, -1).trim();
    }
    const n = parseFloat(t);
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
}

/**
 * Immutable spec for Recharts: deep-copies `data` and `series`, coerces every series metric to a finite number.
 * Does not mutate the input spec.
 * @param {TrodoChartSpec} spec
 * @returns {{ ok: true, spec: TrodoChartSpec } | { ok: false, reason: string }}
 */
export function buildRechartsReadySpec(spec) {
  if (!spec || typeof spec !== "object") {
    return { ok: false, reason: "missing spec" };
  }

  if (spec.kind === "flow_paths") {
    if (!Array.isArray(spec.data) || spec.data.length < 1) {
      return { ok: false, reason: "flow_paths needs at least 1 path" };
    }
    return { ok: true, spec };
  }

  const { xKey, series, data } = spec;
  if (!Array.isArray(data) || data.length < 2) {
    return { ok: false, reason: "data must have at least 2 rows" };
  }
  if (!Array.isArray(series) || series.length < 1) {
    return { ok: false, reason: "need at least one series" };
  }
  if (typeof xKey !== "string" || !xKey.length) {
    return { ok: false, reason: "missing xKey" };
  }

  const seriesCopy = series.map((s) => ({ ...s }));
  const dataCopy = [];
  /** How many rows carry no value for each series — a series of all gaps is not drawable. */
  const seriesGaps = new Map();

  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      return { ok: false, reason: `invalid row ${i}` };
    }
    const out = { ...row };
    if (!(xKey in out)) {
      return { ok: false, reason: `xKey "${xKey}" missing on row ${i}` };
    }
    const xv = out[xKey];
    if (xv == null || (typeof xv !== "string" && typeof xv !== "number")) {
      return { ok: false, reason: `invalid xKey label on row ${i}` };
    }

    for (const s of seriesCopy) {
      const dk = s.dataKey;
      const raw = out[dk];
      // A MISSING VALUE IS A GAP, NOT A BROKEN CHART. A time axis is filled to
      // a continuous range, so a day with no runs carries null — and rejecting
      // the whole spec for it meant any series with a quiet day silently drew
      // nothing at all (ChatInlineChart returns null on !ok, deliberately
      // without console noise). Live: "Show me daily run volume over the last
      // 14 days" came back as a sentence, because 4 of its 15 buckets were
      // null. Recharts draws a break at null, which is the truth: no data, not
      // zero. Anything that is present but not a number is still a real fault.
      if (raw == null || raw === "") {
        out[dk] = null;
        seriesGaps.set(dk, (seriesGaps.get(dk) || 0) + 1);
        continue;
      }
      const n = coerceTrodoChartMetricValue(raw);
      if (!Number.isFinite(n)) {
        return {
          ok: false,
          reason: `non-finite or NaN value for series "${dk}" on row ${i}`,
        };
      }
      out[dk] = n;
    }
    dataCopy.push(out);
  }

  // A series that is nothing but gaps cannot be drawn and must not be claimed
  // in a legend. If that leaves no series at all there is no chart to draw.
  const drawable = seriesCopy.filter((s) => (seriesGaps.get(s.dataKey) || 0) < dataCopy.length);
  if (!drawable.length) {
    return { ok: false, reason: "every series is empty" };
  }

  const next = {
    ...spec,
    series: drawable,
    data: dataCopy,
  };
  return { ok: true, spec: /** @type {TrodoChartSpec} */ (next) };
}

/**
 * Deep-normalize spec: camelCase all row keys; align xKey and series[].dataKey; coerce metrics.
 * @param {Record<string, unknown>} spec
 * @returns {TrodoChartSpec | null}
 */
/** Units the chart's tick formatters know how to render. */
const RENDERABLE_UNITS = new Set(["currency", "percent", "duration_ms", "compact"]);

export function normalizeTrodoChartSpec(spec) {
  if (!spec || typeof spec !== "object" || Array.isArray(spec)) return null;

  const o = /** @type {Record<string, unknown>} */ (spec);
  if (o.version !== 1) return null;
  if (typeof o.kind !== "string" || !SUPPORTED_KINDS.includes(/** @type {typeof SUPPORTED_KINDS[number]} */ (o.kind)))
    return null;
  if (typeof o.title !== "string") return null;

  if (o.kind === "flow_paths") {
    if (!Array.isArray(o.data) || o.data.length < 1) return null;
    for (const row of o.data) {
      if (!row || typeof row !== "object") return null;
      if (!Array.isArray(row.events) || row.events.length < 2) return null;
      if (!Array.isArray(row.counts) || row.counts.length < 2) return null;
      if (typeof row.users !== "number") return null;
    }
    return /** @type {TrodoChartSpec} */ ({
      version: 1,
      kind: "flow_paths",
      title: o.title,
      xKey: "path",
      series: [{ name: "users", dataKey: "users" }],
      data: o.data,
    });
  }

  if (typeof o.xKey !== "string" || !o.xKey.trim()) return null;
  if (!Array.isArray(o.series) || o.series.length < 1) return null;
  if (!Array.isArray(o.data)) return null;

  const xKeyNorm = normalizeLabelKey(o.xKey);
  const seriesNorm = o.series.map((s) => {
    if (!s || typeof s !== "object" || Array.isArray(s)) return null;
    const se = /** @type {Record<string, unknown>} */ (s);
    if (se.dataKey == null || String(se.dataKey).trim() === "") return null;
    const dataKey = normalizeLabelKey(String(se.dataKey));
    const name =
      typeof se.name === "string" && se.name.trim()
        ? se.name.trim()
        : dataKey || "series";
    /** @type {{ name: string; dataKey: string; color?: string }} */
    const item = { name, dataKey };
    if (typeof se.color === "string" && se.color.trim()) {
      item.color = se.color.trim();
    }
    return item;
  });
  if (seriesNorm.some((x) => !x || !x.dataKey)) return null;

  const dataNorm = o.data.map((row) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) return null;
    const r = /** @type {Record<string, unknown>} */ (row);
    const out = {};
    for (const [k, v] of Object.entries(r)) {
      out[normalizeLabelKey(k)] = v;
    }
    return out;
  });
  if (dataNorm.some((x) => x == null)) return null;

  for (const row of dataNorm) {
    for (const s of seriesNorm) {
      if (!s) continue;
      const dk = s.dataKey;
      if (row[dk] !== undefined && row[dk] !== null) {
        row[dk] = coerceTrodoChartMetricValue(row[dk]);
      }
    }
  }

  const out = /** @type {TrodoChartSpec} */ ({
    version: 1,
    kind: o.kind,
    title: o.title,
    xKey: xKeyNorm,
    // The unit every tick formatter and tooltip in ChatInlineChart reads. Like
    // the table parser, this normalizer rebuilds the spec key by key, so an
    // unnamed key is a key the chart never sees — a cost axis drew 0 2 4 while
    // the fence said 'currency' the whole time.
    ...(RENDERABLE_UNITS.has(o.unit) && { unit: o.unit }),
    series: seriesNorm.map((s) =>
      s.color
        ? { name: s.name, dataKey: s.dataKey, color: s.color }
        : { name: s.name, dataKey: s.dataKey }
    ),
    data: dataNorm,
  });
  return out;
}

/**
 * Structural + key alignment checks after normalization.
 * @param {TrodoChartSpec} spec
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export function assertRenderableTrodoChartSpec(spec) {
  if (spec.kind === "flow_paths") {
    return Array.isArray(spec.data) && spec.data.length >= 1
      ? { ok: true }
      : { ok: false, reason: "flow_paths needs at least 1 path" };
  }
  if (!spec.data || spec.data.length < 2) {
    return { ok: false, reason: "data must have at least 2 rows" };
  }
  const { xKey, series, data } = spec;
  if (!xKey || !series?.length) {
    return { ok: false, reason: "missing xKey or series" };
  }

  for (const row of data) {
    if (!(xKey in row)) {
      return { ok: false, reason: `xKey "${xKey}" missing on a data row` };
    }
    const xv = row[xKey];
    if (xv == null || (typeof xv !== "string" && typeof xv !== "number")) {
      return { ok: false, reason: `xKey "${xKey}" has invalid label type` };
    }
    for (const s of series) {
      if (!(s.dataKey in row)) {
        return {
          ok: false,
          reason: `Chart key mismatch: series dataKey "${s.dataKey}" missing on a row`,
        };
      }
      if (!Number.isFinite(/** @type {number} */ (row[s.dataKey]))) {
        return { ok: false, reason: `non-finite value for "${s.dataKey}"` };
      }
    }
  }

  return { ok: true };
}

/**
 * Legacy validator (pre-normalization). Prefer {@link tryParseTrodoChartFence}.
 * @param {unknown} obj
 * @returns {boolean}
 */
export function validateTrodoChartSpec(obj) {
  const n = typeof obj === "object" && obj && !Array.isArray(obj) ? normalizeTrodoChartSpec(/** @type {Record<string, unknown>} */ (obj)) : null;
  if (!n) return false;
  return assertRenderableTrodoChartSpec(n).ok;
}

/**
 * Parse fenced block inner text → normalized, renderable spec or null.
 * @param {string} rawInner
 * @param {{ debug?: boolean }} [options]
 * @returns {TrodoChartSpec | null}
 */
export function tryParseTrodoChartFence(rawInner, options = {}) {
  const cleaned = sanitizeTrodoChartJsonText(rawInner);
  // Empty / partial fence — common while the answer is still streaming in.
  if (!cleaned.length) return null;

  let parsed;
  try {
    parsed = JSON.parse(cleaned);
  } catch {
    // Incomplete JSON mid-stream: this fence re-parses on every chunk and
    // only succeeds once the closing brace arrives. Fail silently until then
    // instead of logging a parse error per chunk.
    return null;
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;

  const normalized = normalizeTrodoChartSpec(/** @type {Record<string, unknown>} */ (parsed));
  if (!normalized) return null;

  const check = assertRenderableTrodoChartSpec(normalized);
  if (!check.ok) return null;

  return normalized;
}
